import { useTenant } from '@parcel2go/npm-p2g-app';
import { useAuth } from 'react-oidc-context';
import { UnauthorisedResponse } from '../unauthorised-response';
import { INVOICE_API } from '~/lib/constants';
import { authedFetch } from '../authed-fetch';
import { parseResult } from '../parse-result';
import { ApiResponse } from '../api-response';
import { GetSignedStatementUrlModel } from './types';
import { useCallback } from 'react';

export const StatementGenerationTimeoutMs = 29000;

export function useInvoiceApi() {
  const { user } = useAuth();
  const { tenant } = useTenant();

  const getSignedStatementUrl = useCallback(
    async (dateFrom: string, dateTo: string, summary?: boolean): Promise<ApiResponse<GetSignedStatementUrlModel>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }

      const url = `${INVOICE_API}/${tenant}/invoice/statement?dateFrom=${encodeURIComponent(dateFrom)}&dateTo=${encodeURIComponent(dateTo)}&summary=${summary}`;
      const response = await authedFetch(tenant, url, {}, user?.access_token, {}, StatementGenerationTimeoutMs);

      return parseResult<GetSignedStatementUrlModel>(response);
    },
    [user?.access_token, tenant]
  );

  const getSignedCsvExportUrl = useCallback(
    async (dateFrom: string, dateTo: string): Promise<ApiResponse<GetSignedStatementUrlModel>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }

      const url = `${INVOICE_API}/${tenant}/invoice/export?dateFrom=${encodeURIComponent(dateFrom)}&dateTo=${encodeURIComponent(dateTo)}`;
      const response = await authedFetch(tenant, url, {}, user?.access_token, {}, StatementGenerationTimeoutMs);

      return parseResult<GetSignedStatementUrlModel>(response);
    },
    [user?.access_token, tenant]
  );

  const getSignedInvoiceUrl = useCallback(
    async (orderId: number): Promise<ApiResponse<GetSignedStatementUrlModel>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }

      const url = `${INVOICE_API}/${tenant}/invoice/authenticated/order?id=${orderId}`;
      const response = await authedFetch(tenant, url, {}, user?.access_token);

      return parseResult<GetSignedStatementUrlModel>(response);
    },
    [user?.access_token, tenant]
  );

  return {
    getSignedStatementUrl,
    getSignedCsvExportUrl,
    getSignedInvoiceUrl
  } as const;
}
